import { getTimeZones } from '@vvo/tzdb';

export interface TimezoneOption {
  /** Canonical IANA id, e.g. "America/Los_Angeles" — the value persisted + validated by the backend. */
  value: string;
  /** Human label, city first, ISO UTC offset, e.g. "Los Angeles (UTC-07:00)". */
  label: string;
  /** Lowercased match blob: IANA id (slashes/underscores → spaces) + city + offset. */
  search: string;
}

/** Current ISO 8601 UTC offset from minutes-east-of-UTC, e.g. 330 → "UTC+05:30", -420 → "UTC-07:00". */
function offsetLabel(offsetInMinutes: number): string {
  const sign = offsetInMinutes < 0 ? '-' : '+';
  const abs = Math.abs(offsetInMinutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `UTC${sign}${hh}:${mm}`;
}

let cache: TimezoneOption[] | null = null;

/**
 * Full searchable IANA timezone list. Sourced from the bundled `@vvo/tzdb` dataset (refreshed on each
 * IANA release via Dependabot) instead of the browser's `Intl.supportedValuesOf('timeZone')`, which is
 * absent on pre-2022 Safari/WebViews and omits `Etc/*` zones on Chrome. `@vvo/tzdb` computes the
 * current DST-aware offset via `Intl.DateTimeFormat` (a ~2016 baseline API) and drops any zone the
 * runtime doesn't recognise, so this never throws on older engines (AI-1121).
 */
export function getTimezoneOptions(): TimezoneOption[] {
  if (cache) return cache;
  cache = getTimeZones({ includeUtc: true })
    .map((tz) => {
      // tzdb names UTC as "Etc/UTC"; keep the canonical "UTC" the backend + saved triggers already use.
      const value = tz.name === 'Etc/UTC' ? 'UTC' : tz.name;
      if (value === 'UTC') {
        return { value, label: 'UTC', search: 'utc', _sort: 0 };
      }
      // City from the IANA id's last segment (e.g. "Asia/Kolkata" → "Kolkata"); tzdb's mainCities[0]
      // sometimes picks a different anchor city (Kolkata → "Mumbai"), so derive it from the id.
      const city = value.split('/').pop()!.replace(/_/g, ' ');
      const offset = offsetLabel(tz.currentTimeOffsetInMinutes);
      const label = `${city} (${offset})`;
      const search = `${value} ${city} ${offset}`.toLowerCase().replace(/[/_]/g, ' ');
      return { value, label, search, _sort: tz.currentTimeOffsetInMinutes };
    })
    .sort((a, b) => a._sort - b._sort || a.value.localeCompare(b.value))
    .map(({ _sort, ...o }) => o);
  return cache;
}

export function getLocalTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function getSuggestedValues(local: string): string[] {
  const valid = new Set(getTimezoneOptions().map((o) => o.value));
  const out: string[] = [];
  if (local !== 'UTC' && valid.has(local)) out.push(local);
  out.push('UTC');
  return out;
}
